import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { Survey } from 'survey-react-ui';
import { withStyles } from '@material-ui/core';
import { Model } from 'survey-core';
import 'survey-core/survey-core.css';
import { PlainLight } from 'survey-core/themes';
import { FormattedMessage, injectIntl, useIntl } from 'react-intl';
import { observer } from 'mobx-react';
import { useUserContext } from '../../Services/Providers';
import endpoints from '../../Services/Api';
import Loading from '../../Components/Loading';
import ErrorAlert from '../../Components/ErrorAlert';
import { useHistory } from 'react-router-dom';
import useStyles from './styles';

const getValueByPath = (obj, path) => {
  const parts = path.split('.');
  let value = obj;

  for (const part of parts) {
    if (value && typeof value === 'object' && part in value) {
      value = value[part];
    } else {
      return null;
    }
  }
  return value;
};

const extractArrayValues = (array) => {
  return array.map((item) => {
    if (item?.name) return item.name;
    if (item?.description) return item.description;
    if (typeof item === 'string' || typeof item === 'number') return item;
    return item;
  });
};

const getElementByName = (page, name) =>
  page?.jsonObj?.elements?.find((e) => e.name === name);

const getConsentDescriptionLocalized = (consentElement, locale = 'en') => {
  if (!consentElement?.description) return '';
  return locale.startsWith('fr')
    ? consentElement.description.fr || ''
    : consentElement.description.default || '';
};

/**
 * Recursively processes SurveyJS JSON schema and replaces placeholders
 * with actual data from the backend API response.
 */
const mapDynamicFields = (obj, codesData, fieldData) => {
  if (obj === null || obj === undefined) {
    return obj;
  }

  if (Array.isArray(obj)) {
    return obj.map((item) => mapDynamicFields(item, codesData, fieldData));
  }

  if (typeof obj === 'object') {
    return Object.entries(obj).reduce((result, [key, value]) => {
      result[key] = mapDynamicFields(value, codesData, fieldData);
      return result;
    }, {});
  }

  if (typeof obj === 'string') {
    // Handle {fieldName} placeholders for code lists
    const codeMatch = obj.match(/^\{(\w+)\}$/);
    if (codeMatch) {
      const fieldName = codeMatch[1];
      if (codesData[fieldName]) {
        return codesData[fieldName];
      }
    }

    // Handle [path.to.field] placeholders for dynamic data
    const pathMatch = obj.match(/^\[(.*?)\]$/);
    if (pathMatch) {
      const path = pathMatch[1];
      const resolved = getValueByPath(fieldData, path);

      if (Array.isArray(resolved)) {
        return extractArrayValues(resolved);
      }

      return resolved;
    }
  }

  return obj;
};

/**
 * Transforms backend code objects into SurveyJS-compatible format.
 * Converts {code, description} objects to {value, text} format.
 */
const transformCodesData = (codesData) => {
  return Object.entries(codesData).reduce((transformed, [key, value]) => {
    if (!Array.isArray(value)) {
      transformed[key] = value;
      return transformed;
    }

    // Transform {code, description} to {value, text}
    if (value.length > 0 && typeof value[0] === 'object' && 'code' in value[0]) {
      transformed[key] = value.map((item) => ({
        value: item.code,
        text: item.description,
      }));
    }
    // Flatten nested arrays
    else if (value.length > 0 && Array.isArray(value[0])) {
      transformed[key] = value.flat();
    }
    // Keep simple arrays as-is
    else {
      transformed[key] = value;
    }

    return transformed;
  }, {});
};

/* ===== No entries announcement helpers (deduped) ===== */

const normalizeWhitespace = (s) =>
  typeof s === 'string'
    ? s
        .replace(/\r\n/g, '\n')
        .replace(/[ \t]+/g, ' ')
        .replace(/\n{3,}/g, '\n\n')
        .trim()
    : '';

const getNoEntriesRenderedText = (surveyModel, locale = 'en') => {
  const loc = locale || surveyModel?.locale || 'en';
  const normalizedLocale = `${loc}`.toLowerCase();

  const tryGetFromLocalization = () => {
    try {
      const l = surveyModel?.localization;
      if (!l) return '';
      const key = 'noEntriesText';
      const res =
        (typeof l.getString === 'function' && l.getString(key, normalizedLocale)) ||
        (typeof l.getString === 'function' && l.getString(key)) ||
        '';
      return normalizeWhitespace(res);
    } catch {
      return '';
    }
  };

  const a = tryGetFromLocalization();
  if (a) return a;

  return normalizeWhitespace('No entries yet.\nClick the button below to add a new entry.');
};

const getPanelDynamicEmptyTextFromDom = (panelRoot) => {
  if (!panelRoot) return '';

  const selectors = [
    '.sd-paneldynamic__empty',
    '.sd-paneldynamic__empty-text',
    '.sd-paneldynamic__emptyText',
    '.sv-paneldynamic__empty',
    '.sv-paneldynamic__empty-text',
    '.sv-paneldynamic__emptyText',
  ];

  for (const sel of selectors) {
    const el = panelRoot.querySelector(sel);
    const txt = normalizeWhitespace(el?.textContent || '');
    if (txt) return txt;
  }

  const allText = Array.from(panelRoot.querySelectorAll('*'))
    .map((n) => normalizeWhitespace(n?.textContent || ''))
    .filter(Boolean);

  const hit = allText.find((t) => /no entries yet|click the button below/i.test(t));
  return hit || '';
};

const ensureSrDescription = (panelRoot, text, srOnlyClass) => {
  if (!panelRoot || !text) return null;

  let id = panelRoot.getAttribute('data-srdesc-id');
  if (!id) {
    id = `srdesc-${Math.random().toString(36).slice(2, 10)}`;
    panelRoot.setAttribute('data-srdesc-id', id);
  }

  let node = panelRoot.querySelector(`#${CSS.escape(id)}`);
  if (!node) {
    node = document.createElement('span');
    node.id = id;
    node.className = srOnlyClass || 'sr-only';
    panelRoot.appendChild(node);
  }

  node.textContent = text;
  return id;
};

class SurveyStorage {
  constructor(intakeAppointmentId) {
    this.intakeAppointmentId = intakeAppointmentId;
  }

  async saveProgress(data, currentPageNo, language, currentPageName = null) {
    try {
      const payload = {
        idIntakeAppointment: this.intakeAppointmentId,
        data: JSON.stringify({
          data,
          currentPageNo,
          currentPageName,
          lastSaved: new Date().toISOString(),
          language,
        }),
      };
      await endpoints.intakeAppointment.save(payload);
      return true;
    } catch (error) {
      return false;
    }
  }

  async saveFinal(data, language) {
    try {
      //TODO: submit intake form
      return true;
    } catch (error) {
      return false;
    }
  }
}

const SurveyForm = ({ match, classes }) => {
  const [surveyJson, setSurveyJson] = useState(null);
  const [formData, setFormData] = useState(null);
  const [intakeAppointmentId, setIntakeAppointmentId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const { userContextObj } = useUserContext();
  const history = useHistory();
  const intl = useIntl();
  const appointmentId = match.params?.id;

  /* ===== screen reader refs ===== */

  const srRef = useRef(null);
  const hasAnnouncedRef = useRef(false);
  const lastAnnouncementRef = useRef('');

  // used only for cleanup compatibility (we no longer add focus listeners)
  const focusHandlersRef = useRef(new Map());

  // dedupe / throttle for enhance()
  const enhanceRafRef = useRef(null);
  const describedRef = useRef(new WeakMap());

  const announce = useCallback((message) => {
    const msg = typeof message === 'string' ? message.trim() : '';
    if (!srRef.current || !msg) return;
    if (lastAnnouncementRef.current === msg) return;

    lastAnnouncementRef.current = msg;
    srRef.current.textContent = '';

    requestAnimationFrame(() => {
      if (!srRef.current) return;
      srRef.current.textContent = msg;
    });
  }, []);

  const focusFirstQuestion = useCallback(() => {
    requestAnimationFrame(() => {
      const el = document.querySelector(
        [
          '.sd-page--active input:not([type="hidden"])',
          '.sd-page--active textarea',
          '.sd-page--active select',
          '.sd-page--active [tabindex]:not([tabindex="-1"])',
          '.sd-page--active button',
        ].join(', '),
      );
      el?.focus?.({ preventScroll: true });
    });
  }, []);

  const storage = useMemo(() => {
    if (!intakeAppointmentId) return null;
    return new SurveyStorage(intakeAppointmentId);
  }, [intakeAppointmentId]);

  // Fetch required data and build survey schema
  useEffect(() => {
    const fetchCodesAndBuildSurvey = async () => {
      try {
        setLoading(true);
        setError(null);

        const intakeResp = await endpoints.intakeAppointment.get(
          { appointmentId },
          { 'Accept-Language': intl.locale },
        );

        const intakeAppointments = intakeResp?.data;
        if (!intakeAppointments || intakeAppointments.length === 0) {
          setError('No intake appointment found');
          return;
        }

        const intakeAppointment = intakeAppointments[0];
        const formJson = JSON.parse(intakeAppointment?.formComposer?.data || '{}');
        const formValues = JSON.parse(intakeAppointment?.data || '{}');

        setFormData(formValues);
        setIntakeAppointmentId(intakeAppointment.idIntakeAppointment);

        // Fetch code lists in parallel with dynamic dependencies
        const [codeListResponse, dynamicDepsResponse] = await Promise.all([
          endpoints.codeList.get(
            { accountId: userContextObj.accountId },
            { 'Accept-Language': intl.locale },
          ),
          endpoints.dynamicDependencies.get(
            { intakeAppointmentId: intakeAppointment.idIntakeAppointment },
            { 'Accept-Language': intl.locale },
          ),
        ]);

        const transformedCodes = transformCodesData(codeListResponse.data);
        const processedSchema = mapDynamicFields(
          formJson,
          transformedCodes,
          dynamicDepsResponse?.data,
        );
        setSurveyJson(processedSchema);
      } catch (err) {
        setError(err.message || 'Failed to load survey');
      } finally {
        setLoading(false);
      }
    };

    if (appointmentId && userContextObj.accountId) {
      fetchCodesAndBuildSurvey();
    }
  }, [appointmentId, intl.locale, userContextObj.accountId]);

  const survey = useMemo(() => {
    if (!surveyJson) return null;

    const model = new Model(surveyJson);

    model.applyTheme(PlainLight);
    model.locale = intl.locale;

    // Restore saved progress
    const { data, currentPageNo } = formData || {};
    if (data) model.data = data;
    if (currentPageNo !== null && currentPageNo !== undefined) model.currentPageNo = currentPageNo;

    // Reset announcement flag when survey or locale changes
    hasAnnouncedRef.current = false;
    lastAnnouncementRef.current = '';

    return model;
  }, [surveyJson, formData, intl.locale]);

  const enhancePanelDynamicAddButtons = useCallback(
    (surveyModel) => {
      if (!surveyModel) return;

      const sm = surveyModel;
      const fallback = getNoEntriesRenderedText(sm, intl.locale);

      const run = () => {
        const activePageRoot =
          document.querySelector('.sd-page--active') ||
          document.querySelector('.sv_page') ||
          document;

        const panelDynamics = activePageRoot.querySelectorAll(
          '.sd-question--paneldynamic, .sv_q_paneldynamic, .sd-paneldynamic, .sv-paneldynamic',
        );

        panelDynamics.forEach((panelRoot) => {
          const emptyText = getPanelDynamicEmptyTextFromDom(panelRoot) || fallback;
          if (!emptyText) return;

          const addBtn =
            panelRoot.querySelector(
              'button[aria-label*="Add"], button[title*="Add"], .sd-paneldynamic__btn-add, .sv-paneldynamic__add-btn',
            ) ||
            Array.from(panelRoot.querySelectorAll('button')).find((b) =>
              /\badd\b|\+\s*add/i.test((b.textContent || '').trim()),
            );

          if (!addBtn) return;

          const lastApplied = describedRef.current.get(addBtn);
          if (lastApplied === emptyText) return;

          const descId = ensureSrDescription(
            panelRoot,
            emptyText,
            classes?.sronly || 'sr-only',
          );

          if (descId) {
            const prev = (addBtn.getAttribute('aria-describedby') || '').trim();
            const parts = prev ? prev.split(/\s+/) : [];
            if (!parts.includes(descId)) {
              addBtn.setAttribute('aria-describedby', [...parts, descId].join(' '));
            }
          }

          describedRef.current.set(addBtn, emptyText);

          // IMPORTANT: no manual announce on focus (prevents double-reading)
          if (focusHandlersRef.current.has(addBtn)) {
            const handler = focusHandlersRef.current.get(addBtn);
            try {
              addBtn.removeEventListener('focus', handler, true);
            } catch {}
            focusHandlersRef.current.delete(addBtn);
          }
        });
      };

      if (enhanceRafRef.current) cancelAnimationFrame(enhanceRafRef.current);
      enhanceRafRef.current = requestAnimationFrame(run);
    },
    [classes?.sronly, intl.locale],
  );

  /* INITIAL RENDER: announce only once*/
  useEffect(() => {
    if (!survey || hasAnnouncedRef.current) return;

    const page = survey.currentPage;
    const title = (survey.title || '').trim();
    const desc = (survey.description || '').trim();
    const pageTitle = (survey.currentPage?.title || '').trim();
    const pageDesc = (survey.currentPage?.description || '').trim();

    const message = [];

    if (title) message.push(title);
    if (desc) message.push(desc);
    if (pageTitle) message.push(pageTitle);
    if (pageDesc) message.push(pageDesc);

    const consentElement = getElementByName(page, 'consent_subtitle');
    const locale = survey.locale || 'en';
    const consentDescriptionLocalized = getConsentDescriptionLocalized(consentElement, locale);

    if (consentDescriptionLocalized) {
      message.push(consentDescriptionLocalized);
    }
    if (!message.length) return;

    const id = setTimeout(() => {
      announce(message.join('. '));
      hasAnnouncedRef.current = true;
      enhancePanelDynamicAddButtons(survey);
    }, 100);

    return () => clearTimeout(id);
  }, [survey, announce, enhancePanelDynamicAddButtons]);

  /* ===== survey events ===== */
  useEffect(() => {
    if (!survey || !storage) return;

    const isConsentValid = (surveyModel) => {
      const locale = surveyModel.locale || 'en';
      const value = surveyModel.getValue('consent_confirmation');
      const normalizedValue = (value || '').trim().toLowerCase();
      const requiredWord = locale.startsWith('fr') ? 'accepte' : 'consent';
      return normalizedValue === requiredWord;
    };

    const onComplete = async (sender) => {
      if (!isConsentValid(sender)) {
        const msg = intl.formatMessage({ id: 'consentError' });
        setError('consentError');
        announce(msg);
        return;
      }

      storage.saveFinal(sender.data, intl.locale);
      setLoading(true);
      setError(null);

      try {
        const res = await endpoints.submitIntakeAppointment.put(
          {
            idIntakeAppointment: intakeAppointmentId,
            data: JSON.stringify({ data: sender.data }),
          },
          {
            'Accept-Language': intl.locale,
          },
        );

        if (res.ok) {
          setLoading(false);
          // Set success state to display success message
          setSubmitSuccess(true);
        } else {
          throw new Error(res.problem || 'Failed to submit intake form');
        }
      } catch (err) {
        setError(err.message || 'Failed to submit survey');
        setLoading(false);
      }
    };

    const validateConsent = (sender, options) => {
      if (options.name !== 'consent_confirmation') return;

      if (!isConsentValid(sender)) {
        const msg = intl.formatMessage({ id: 'consentError' });
        options.error = msg;
        announce(msg);
      } else {
        options.error = null;
      }
    };

    const setLanguage = (sender) => {
      sender.setValue('lang', sender.locale);
    };

    const onCurrentPageChanged = (sender, options) => {
      storage.saveProgress(
        sender.data,
        sender.currentPageNo,
        intl.locale,
        options.newCurrentPage?.name,
      );

      const page = sender.currentPage;
      const pageTitle = page?.title || '';
      const pageDesc = page?.description || '';
      const consentElement = getElementByName(page, 'consent_subtitle');
      const locale = sender.locale || 'en';
      const consentDescriptionLocalized = getConsentDescriptionLocalized(consentElement, locale);

      if (page?.name === 'consent_page') {
        const message = [pageTitle, pageDesc, consentDescriptionLocalized]
          .filter(Boolean)
          .join('. ');
        if (message) announce(message);
      } else if (pageTitle) {
        const message = [pageTitle, pageDesc].filter(Boolean).join('. ');
        announce(message);
      }

      focusFirstQuestion();
      enhancePanelDynamicAddButtons(sender);
    };

    const afterRender = (sender) => {
      enhancePanelDynamicAddButtons(sender);
    };

    survey.onAfterRenderSurvey.add(setLanguage);
    survey.onAfterRenderSurvey.add(afterRender);
    survey.onValidateQuestion.add(validateConsent);
    survey.onComplete.add(onComplete);
    survey.onCurrentPageChanged.add(onCurrentPageChanged);

    return () => {
      survey.onAfterRenderSurvey.remove(setLanguage);
      survey.onAfterRenderSurvey.remove(afterRender);
      survey.onValidateQuestion.remove(validateConsent);
      survey.onComplete.remove(onComplete);
      survey.onCurrentPageChanged.remove(onCurrentPageChanged);

      if (enhanceRafRef.current) cancelAnimationFrame(enhanceRafRef.current);
      enhanceRafRef.current = null;

      const handlers = focusHandlersRef.current;
      handlers.forEach((handler, el) => {
        try {
          el.removeEventListener('focus', handler, true);
        } catch {}
      });
      handlers.clear();
    };
  }, [
    survey,
    storage,
    intl,
    intl.locale,
    intakeAppointmentId,
    focusFirstQuestion,
    announce,
    enhancePanelDynamicAddButtons,
  ]);

  useEffect(() => {
    if (submitSuccess) history.push('/appointments', { from: 'surveySubmitSuccess' });
  }, [submitSuccess, history]);

  if (loading) {
    return <Loading />;
  }

  if (error) {
    const errorMessage = `${error}`;
    return <ErrorAlert messageId={errorMessage} />;
  }

  if (!survey) return <ErrorAlert messageId="noSurveyError" />;

  return (
    <div className={classes.intakeFormContainer}>
      <div
        ref={srRef}
        aria-live="polite"
        aria-atomic="true"
        role="status"
        className={classes?.sronly || 'sr-only'}
      />

      <Survey model={survey} />
    </div>
  );
};

export default withStyles(useStyles)(observer(injectIntl(SurveyForm)));